/**
 * Vue Router Configuration
 * CD-269: Setup Vue Router
 * CD-273: Create route guards
 */
import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import userRoutes from './userRoutes'
import adminRoutes from './adminRoutes'

// Detect if this is admin domain or user domain
const isAdminDomain = window.location.hostname.startsWith('admin.') || window.location.hostname.startsWith('admindev.')

// Select routes based on domain
const routes = isAdminDomain ? adminRoutes : userRoutes

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // Scroll to saved position (back/forward) or top
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  }
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // CD-362: Wait for auth initialization before checking auth state
  await authStore.waitForInit()

  // Update document title
  document.title = to.meta.title
    ? `${to.meta.title} | Clone Detector`
    : 'Clone Detector'

  // Check if route requires authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    // Redirect to login with return URL
    return next({
      name: 'login',
      query: { redirect: to.fullPath }
    })
  }

  // Check if route requires admin access
  if (to.meta.requiresAdmin && !authStore.isAdmin) {
    return next({ name: 'forbidden' })
  }

  // Check if route is for guests only (login, register, etc.)
  if (to.meta.guestOnly && authStore.isAuthenticated) {
    // Redirect authenticated users to dashboard
    return next({ name: isAdminDomain ? 'admin-dashboard' : 'dashboard' })
  }

  // Onboarding guard — redirect social sign-up users to complete profile before accessing app
  if (
    to.meta.requiresAuth &&
    authStore.isAuthenticated &&
    authStore.user?.onboarding_complete === false &&
    to.name !== 'onboarding'
  ) {
    return next({ name: 'onboarding' })
  }

  next()
})

// After navigation - GA4 page view tracking
router.afterEach((to, from) => {
  if (typeof gtag === 'function') {
    gtag('event', 'page_view', {
      page_path: to.fullPath,
      page_title: to.meta.title || document.title,
      page_location: window.location.origin + to.fullPath
    })
  }
})

export default router
